import BaseButton from 'components/ui/BaseButton';
import EmailInput from 'components/common/inputs/EmailInput';
import { AUTH_ACTION_TYPE } from 'enums';
import { useContext, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import { t } from 'services/i18n.service';
import { resetPassword, sendEmailVerification } from 'services/auth.service';
import { toast } from 'react-toastify';
import {
  AUTH_SCREEN,
  EMAIL_VERIFICATION_COOL_DOWN,
  TIME_UNIT_CONVERT_FACTOR
} from 'constants/general';
import { decrement } from 'utils/helpers';
import AuthActionHeader from 'dashboard/auth/AuthActionHeader';
import { AuthPageContext } from './AuthActionsWrapper';
import { ATTRIB } from 'constants/test';

const ForgotPassword = () => {
  const {
    state: {
      isProcessing,
      isValidEmail,
      screen,
      email,
      isEmailVerified,
      coolDown
    },
    dispatch
  } = useContext(AuthPageContext);
  const isSelected = useMemo(
    () => screen === AUTH_SCREEN.FORGOT_PASSWORD,
    [screen]
  );

  useEffect(() => {
    if (coolDown <= EMAIL_VERIFICATION_COOL_DOWN.START) {
      return;
    }
    const interval = setInterval(() => {
      dispatch?.({
        type: AUTH_ACTION_TYPE.UPDATE_COOL_DOWN,
        value: decrement(coolDown)
      });
    }, TIME_UNIT_CONVERT_FACTOR.ONE_SECOND_AS_MILLISECONDS);

    return () => clearInterval(interval);
  }, [coolDown, dispatch]);

  const handleSubmit = async () => {
    dispatch?.({ type: AUTH_ACTION_TYPE.UPDATE_IS_PROCESSING, value: true });
    if (isEmailVerified) {
      resetPassword(email, (hasError, error, isEmailNotVerified) => {
        dispatch?.({ type: AUTH_ACTION_TYPE.UPDATE_IS_PROCESSING });
        if (hasError) {
          dispatch?.({
            type: AUTH_ACTION_TYPE.UPDATE_IS_EMAIL_VERIFIED,
            value: !isEmailNotVerified
          });
          toast.error(
            isEmailNotVerified
              ? t(
                  'auth_page.you_need_to_verify_your_email_address_to_reset_your_password'
                )
              : error
          );
        } else {
          dispatch?.({
            type: AUTH_ACTION_TYPE.UPDATE_COOL_DOWN,
            value: EMAIL_VERIFICATION_COOL_DOWN.RESET
          });
          toast.success(
            t('auth_page.we_ve_emailed_you_a_link_to_reset_your_password')
          );
        }
      });
    } else {
      sendEmailVerification(email, (hasError, error) => {
        dispatch?.({ type: AUTH_ACTION_TYPE.UPDATE_IS_PROCESSING });
        if (hasError) {
          toast.error(error);
        } else {
          dispatch?.({
            type: AUTH_ACTION_TYPE.UPDATE_COOL_DOWN,
            value: EMAIL_VERIFICATION_COOL_DOWN.RESET
          });
          toast.success(
            t('auth_page.we_have_sent_a_verification_email_your_way')
          );
        }
      });
    }
  };

  const isForgotPasswordDisabled = useMemo(
    () => !!coolDown || isProcessing || !isValidEmail,
    [coolDown, isProcessing, isValidEmail]
  );

  const resetPasswordBtnTitle = useMemo(() => {
    if (coolDown) {
      return t('auth_page.hang_tight_you_can_try_aga_in_seconds', {
        second: coolDown
      });
    } else if (isEmailVerified) {
      return t('auth_page.reset_password');
    } else {
      return t('auth_page.verify');
    }
  }, [isEmailVerified, coolDown]);

  return (
    <form
      className={classNames(
        'w-10/12 h-fit max-h-[95vh] flex-col ~gap-4/8 animate-fadeIn overflow-y-auto',
        {
          'inline-flex': isSelected,
          [`hidden`]: !isSelected
        }
      )}
      onSubmit={(event) => {
        event.preventDefault();
        handleSubmit();
      }}>
      <AuthActionHeader
        title={t('auth_page.forgot_password')}
        subTitle={t(
          'auth_page.enter_your_email_address_and_we_will_send_you_instructions_to_rest_your_password'
        )}
      />
      <EmailInput
        value={email}
        onChange={({ target: { value } }) =>
          dispatch?.({ type: AUTH_ACTION_TYPE.UPDATE_EMAIL, value })
        }
        readOnly={isProcessing}
        data-testid={ATTRIB.TEST.INPUT_EMAIL_FORGOT_PASSWORD}
      />
      <BaseButton
        type='submit'
        title={isProcessing ? '' : resetPasswordBtnTitle}
        additionalStyles='py-2 shadow-md'
        disabled={isForgotPasswordDisabled}
        icon={
          isProcessing ? (
            <span className='~w-4/6 ~h-4/6 rounded-full animate-spin border-t-2 border-r border-b-2 border-l-4 border-primary-500'></span>
          ) : undefined
        }
        data-testid={ATTRIB.TEST.FORGOT_PASSWORD_RESET_SUBMIT}
      />
      <BaseButton
        title={t('auth_page.back_to_sign_in')}
        additionalStyles='py-2 mt-6 hover:!scale-100'
        variant='tertiary'
        onClick={() =>
          dispatch?.({ type: AUTH_ACTION_TYPE.NAVIGATE_TO_SIGN_IN })
        }
        disabled={isProcessing}
      />
    </form>
  );
};

export default ForgotPassword;
